import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Share, MonitorSmartphone, Download, MessageCircle, CheckCircle2, RefreshCw, Zap } from "lucide-react";
import { toast } from "sonner";
import { onInstallAvailable, promptInstall, isStandalone as standaloneCheck, updateApp } from "../lib/pwaInstall";

/* Permanent "flat link" install page: travelerpulse.pro/get-app
   Always available — QR + short URL + per-platform steps + WhatsApp send.
   Works even when the install banner is snoozed or unsupported. */
export default function GetApp() {
  const [canPrompt, setCanPrompt] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => onInstallAvailable(setCanPrompt), []);

  const appLink = window.location.origin; // direct site link — no shortener
  const waHref = `https://wa.me/?text=${encodeURIComponent(
    `Install the TravelerPulse app \u{1F4F1} Open this link on your phone, then tap Share \u2192 "Add to Home Screen":\n${appLink}`)}`;
  const installNow = async () => {
    const outcome = await promptInstall();
    if (outcome === "accepted") toast.success("TravelerPulse is installing — check your home screen!");
  };
  const refreshApp = () => { setUpdating(true); updateApp(); };

  const standalone = standaloneCheck();
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

  return (
    <div className="max-w-3xl mx-auto px-5 py-10" data-testid="get-app-page">
      <div className="flex items-center gap-4 mb-2">
        <img src="/pwa-icon-192.png" alt="TravelerPulse" className="w-16 h-16 rounded-2xl shadow" />
        <div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight">Get the app</h1>
          <p className="text-sm text-muted-foreground">TravelerPulse on your home screen — full-screen, offline-ready, no app store needed.</p>
        </div>
      </div>

      {/* PROMINENT 1-click install / update — always at the very top */}
      <div className="mt-5 rounded-2xl bg-blue-800 text-white p-5 sm:p-6" data-testid="get-app-oneclick-banner">
        {standalone ? (
          <>
            <div className="flex items-center gap-2 font-heading font-900 text-xl"><CheckCircle2 className="w-5 h-5 text-emerald-400" /> You're using the installed app</div>
            <p className="mt-1 text-sm text-blue-100">The app updates itself automatically. Want the very latest version right now? One tap:</p>
            <button onClick={refreshApp} disabled={updating} data-testid="get-app-update-now"
              className="mt-3 inline-flex items-center gap-2 bg-emerald-500 text-white px-5 py-3 text-sm font-800 rounded-full hover:bg-emerald-600 disabled:opacity-60">
              <RefreshCw className={`w-4 h-4 ${updating ? "animate-spin" : ""}`} /> {updating ? "Updating…" : "Update to the latest version"}
            </button>
          </>
        ) : canPrompt ? (
          <>
            <div className="flex items-center gap-2 font-heading font-900 text-xl"><Zap className="w-5 h-5 text-amber-400" /> Install in one tap — right now</div>
            <p className="mt-1 text-sm text-blue-100">No app store, no steps to follow. Tap the button and confirm — done.</p>
            <button onClick={installNow} data-testid="get-app-install-now"
              className="mt-3 inline-flex items-center gap-2 bg-orange-500 text-white px-6 py-3 text-sm font-800 rounded-full hover:bg-orange-600">
              <Download className="w-4 h-4" /> Install TravelerPulse now
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 font-heading font-900 text-xl"><Zap className="w-5 h-5 text-amber-400" /> {isIOS ? "2 taps and it's installed" : "One-tap install"}</div>
            <p className="mt-1 text-sm text-blue-100">
              {isIOS
                ? <>On iPhone: tap the <b>Share</b> icon below, then <b>"Add to Home Screen"</b>. Apple doesn't allow an install button — these 2 taps are the official way.</>
                : <>On Android, open this page in <b>Chrome</b> and an Install option appears automatically (or use ⋮ menu → "Install app"). On a computer? Scan the QR below with your phone.</>}
            </p>
            <p className="mt-2 text-xs text-blue-200 flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Already installed? The app keeps itself updated automatically — no action needed.</p>
          </>
        )}
      </div>

      {/* QR + short link + actions */}
      <div className="mt-6 border border-zinc-300 bg-white p-6 flex flex-wrap items-center gap-6" data-testid="get-app-qr-card">
        <div className="shrink-0 bg-white p-3 border border-zinc-200 rounded-xl shadow-sm">
          <QRCodeSVG value={appLink} size={160} level="M" data-testid="get-app-qr-local" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-heading font-900 text-xl">Scan with your phone camera</div>
          <p className="text-sm text-muted-foreground mt-1">It opens TravelerPulse on your phone — then follow the steps below for your device.</p>
          <div className="mt-2 text-sm font-mono font-bold text-primary break-all" data-testid="get-app-short-link">
            {appLink.replace("https://", "")}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <a href={waHref} target="_blank" rel="noreferrer" data-testid="get-app-whatsapp"
              className="inline-flex items-center gap-1.5 bg-[#25D366] text-white px-4 py-2.5 text-sm font-700 rounded-full hover:opacity-90">
              <MessageCircle className="w-4 h-4" /> Send the link to my phone (WhatsApp)
            </a>
          </div>
        </div>
      </div>

      {/* Per-platform steps */}
      <div className="mt-6 grid sm:grid-cols-3 gap-4">
        <div className="border border-zinc-300 bg-white p-5" data-testid="get-app-ios">
          <Share className="w-6 h-6 text-primary mb-2" />
          <div className="font-heading font-900">iPhone / iPad</div>
          <ol className="mt-2 text-sm text-zinc-700 space-y-1.5 list-decimal list-inside">
            <li>Open this page in <b>Safari or Chrome</b></li>
            <li>Tap the <b>Share</b> icon (square with ↑)</li>
            <li>Tap <b>"Add to Home Screen"</b> → Add</li>
          </ol>
          <p className="mt-2 text-xs text-muted-foreground">Apple doesn't allow an Install button — these 3 taps are the official way.</p>
        </div>
        <div className="border border-zinc-300 bg-white p-5" data-testid="get-app-android">
          <Smartphone className="w-6 h-6 text-primary mb-2" />
          <div className="font-heading font-900">Android</div>
          <ol className="mt-2 text-sm text-zinc-700 space-y-1.5 list-decimal list-inside">
            <li>Open this page in <b>Chrome</b></li>
            <li>Tap <b>Install</b> when prompted (or ⋮ menu → <b>"Install app"</b>)</li>
            <li>Confirm — the icon appears on your home screen</li>
          </ol>
        </div>
        <div className="border border-zinc-300 bg-white p-5" data-testid="get-app-desktop">
          <MonitorSmartphone className="w-6 h-6 text-primary mb-2" />
          <div className="font-heading font-900">On a computer?</div>
          <p className="mt-2 text-sm text-zinc-700">Scan the QR above with your phone camera, or send yourself the link on WhatsApp — then install on the phone.</p>
        </div>
      </div>

      <p className="mt-6 text-xs text-muted-foreground">
        Once installed, TravelerPulse opens full-screen with its own icon — no browser bars — and your saved trips keep working offline. Long-press the icon for shortcuts like <b>My Trip Today</b>.
      </p>
    </div>
  );
}
